/**
 * Variant Image Filter for Palo Alto Theme
 *
 * Filters product images by color variant based on media alt text.
 * Images with no alt text are treated as universal (shown for all colors).
 * Supports both carousel (Flickity) and tall/stacked layout modes.
 */
(function () {
  'use strict';

  var COLOR_NAMES = ['color', 'colour', '顏色'];

  function isColorOption(name) {
    return COLOR_NAMES.indexOf(name.toLowerCase().trim()) !== -1;
  }

  function initVariantImageFilter(container) {
    var jsonScript = container.querySelector('[data-product-json]');
    if (!jsonScript) return;

    var product;
    try {
      product = JSON.parse(jsonScript.textContent);
    } catch (e) {
      return;
    }

    // Find color option index (0-based)
    var colorOptionIndex = -1;
    if (product.options && product.options.length) {
      for (var i = 0; i < product.options.length; i++) {
        var optName =
          typeof product.options[i] === 'string'
            ? product.options[i]
            : product.options[i].name || '';
        if (isColorOption(optName)) {
          colorOptionIndex = i;
          break;
        }
      }
    }

    // No color option → exit (single-color products unaffected)
    if (colorOptionIndex === -1) return;

    var isTallLayout = container.getAttribute('data-tall-layout') === 'true';
    var slider = container.querySelector('[data-product-single-media-slider]');
    var thumbsContainer = container.querySelector('[data-product-single-media-thumbs]');

    if (!slider) return;

    var lastFilteredColor = null;

    // Build media id → alt color mapping from product JSON
    var mediaColorMap = {};
    if (product.media && product.media.length) {
      product.media.forEach(function (m) {
        if (m.alt) {
          mediaColorMap[String(m.id)] = m.alt.trim().toLowerCase();
        }
      });
    }

    // Record original slide order
    var allSlides = Array.from(slider.querySelectorAll('[data-product-slide]'));
    var originalSlideOrder = allSlides.map(function (slide) {
      return { el: slide, id: slide.getAttribute('data-id') };
    });

    // Record original thumbnail order
    var originalThumbOrder = [];
    if (thumbsContainer) {
      var allThumbs = Array.from(
        thumbsContainer.querySelectorAll('.product-single__thumbnail')
      );
      originalThumbOrder = allThumbs.map(function (thumb) {
        return { el: thumb, id: thumb.getAttribute('data-id') };
      });
    }

    // Create hidden containers for carousel mode (to preserve filtered-out DOM nodes)
    var hiddenSlideContainer = null;
    var hiddenThumbContainer = null;
    if (!isTallLayout) {
      hiddenSlideContainer = document.createElement('div');
      hiddenSlideContainer.style.display = 'none';
      hiddenSlideContainer.setAttribute('aria-hidden', 'true');
      slider.parentNode.insertBefore(hiddenSlideContainer, slider.nextSibling);

      if (thumbsContainer) {
        hiddenThumbContainer = document.createElement('div');
        hiddenThumbContainer.style.display = 'none';
        hiddenThumbContainer.setAttribute('aria-hidden', 'true');
        thumbsContainer.parentNode.insertBefore(
          hiddenThumbContainer,
          thumbsContainer.nextSibling
        );
      }
    }

    function getSlideColor(slide) {
      var id = slide.getAttribute('data-id');
      if (mediaColorMap[id]) return mediaColorMap[id];
      // Fallback: read img alt directly from DOM
      var img = slide.querySelector('img');
      if (img && img.getAttribute('alt')) {
        return img.getAttribute('alt').trim().toLowerCase();
      }
      return null; // No alt = universal image (e.g. size chart)
    }

    function matchesColor(slideColor, selectedColor) {
      if (!slideColor) return true; // No alt text → show for all colors
      if (slideColor === selectedColor) return true;
      // Suffix match: "A YOGI T-Shirt 白" or "A YOGI T-Shirt-白" matches "白"
      return slideColor.endsWith(' ' + selectedColor) || slideColor.endsWith('-' + selectedColor);
    }

    function destroyFlickity(element) {
      if (typeof Flickity === 'undefined') return;
      var instance = Flickity.data(element);
      if (instance) instance.destroy();
    }

    function reinitFlickity(visibleSlideCount) {
      if (typeof Flickity === 'undefined') return;
      // Theme doesn't init Flickity for single slide
      if (visibleSlideCount <= 1) return;

      new Flickity(slider, {
        wrapAround: true,
        pageDots: false,
        adaptiveHeight: true,
      });

      if (thumbsContainer) {
        var visibleThumbs =
          thumbsContainer.querySelectorAll('.product-single__thumbnail');
        if (visibleThumbs.length > 1) {
          new Flickity(thumbsContainer, {
            asNavFor: slider,
            contain: true,
            pageDots: false,
            prevNextButtons: false,
            resize: true,
          });
        }
      }
    }

    // --- Carousel mode (Flickity) ---
    function filterImagesCarousel(color) {
      // 1. Destroy existing Flickity instances (nav first, then main)
      if (thumbsContainer) destroyFlickity(thumbsContainer);
      destroyFlickity(slider);

      // 2. Restore all slides to slider in original order
      originalSlideOrder.forEach(function (item) {
        slider.appendChild(item.el);
      });

      // 3. Restore all thumbnails in original order
      if (thumbsContainer) {
        originalThumbOrder.forEach(function (item) {
          thumbsContainer.appendChild(item.el);
        });
      }

      // 4. Partition slides into matching / non-matching
      var matchingSlides = [];
      var nonMatchingSlides = [];
      originalSlideOrder.forEach(function (item) {
        var slideColor = getSlideColor(item.el);
        if (matchesColor(slideColor, color)) {
          matchingSlides.push(item);
        } else {
          nonMatchingSlides.push(item);
        }
      });

      // Safety: if nothing matches, show all
      if (matchingSlides.length === 0) {
        originalSlideOrder.forEach(function (item, index) {
          item.el.classList.toggle('media--hidden', index !== 0);
        });
        reinitFlickity(originalSlideOrder.length);
        return;
      }

      // 5. Move non-matching slides to hidden container
      nonMatchingSlides.forEach(function (item) {
        hiddenSlideContainer.appendChild(item.el);
      });

      // 6. Sync thumbnails — hide thumbnails whose media is filtered out
      if (thumbsContainer && hiddenThumbContainer) {
        var matchingIds = {};
        matchingSlides.forEach(function (s) {
          matchingIds[s.id] = true;
        });
        originalThumbOrder.forEach(function (item) {
          if (!matchingIds[item.id]) {
            hiddenThumbContainer.appendChild(item.el);
          }
        });
      }

      // 7. Update media--hidden class (first visible = active)
      matchingSlides.forEach(function (item, index) {
        item.el.classList.toggle('media--hidden', index !== 0);
      });

      // 8. Re-initialize Flickity
      reinitFlickity(matchingSlides.length);
    }

    // --- Tall / stacked mode (no Flickity) ---
    function filterImagesTall(color) {
      var matchCount = 0;
      originalSlideOrder.forEach(function (item) {
        var slideColor = getSlideColor(item.el);
        if (matchesColor(slideColor, color)) matchCount++;
      });

      var showAll = matchCount === 0;
      var firstVisible = true;

      originalSlideOrder.forEach(function (item) {
        var slideColor = getSlideColor(item.el);
        var show = showAll || matchesColor(slideColor, color);

        item.el.style.display = show ? '' : 'none';

        if (show && firstVisible) {
          item.el.classList.remove('media--hidden');
          firstVisible = false;
        } else if (show) {
          item.el.classList.add('media--hidden');
        }
      });

      // Sync thumbnails
      if (thumbsContainer) {
        var visibleIds = {};
        originalSlideOrder.forEach(function (item) {
          if (item.el.style.display !== 'none') {
            visibleIds[item.id] = true;
          }
        });
        originalThumbOrder.forEach(function (item) {
          item.el.style.display = visibleIds[item.id] ? '' : 'none';
        });
      }
    }

    // --- Main filter dispatcher ---
    function filterImages(color) {
      if (!color) return;
      var normalized = color.toLowerCase().trim();
      if (normalized === lastFilteredColor) return; // Skip if same color (e.g. size change)
      lastFilteredColor = normalized;

      if (isTallLayout) {
        filterImagesTall(normalized);
      } else {
        filterImagesCarousel(normalized);
      }
    }

    // --- Event listeners ---

    // Primary: theme:variant:change (fired by ProductAddForm.fireHookEvent)
    container.addEventListener('theme:variant:change', function (e) {
      if (!e.detail || !e.detail.variant) return;
      var variant = e.detail.variant;
      var color = variant.options[colorOptionIndex];
      if (color) {
        // Run after theme's own variant-change handlers complete
        setTimeout(function () {
          filterImages(color);
        }, 0);
      }
    });

    // Backup: listen for direct option selector changes
    container.addEventListener('change', function (e) {
      var target = e.target;
      if (!target.hasAttribute('data-single-option-selector')) return;
      var dataIndex = parseInt(target.getAttribute('data-index'), 10);
      // Palo Alto uses 1-based data-index (option.position)
      if (dataIndex - 1 === colorOptionIndex) {
        setTimeout(function () {
          filterImages(target.value);
        }, 10);
      }
    });

    // Initial filter based on currently selected variant
    function initialFilter() {
      var variantInput = container.querySelector(
        'select[name="id"], input[name="id"]'
      );
      if (!variantInput || !product.variants) return;

      var vid = parseInt(variantInput.value, 10);
      if (!vid) return;

      var currentVariant = null;
      for (var j = 0; j < product.variants.length; j++) {
        if (product.variants[j].id === vid) {
          currentVariant = product.variants[j];
          break;
        }
      }

      if (currentVariant) {
        var initialColor = currentVariant.options[colorOptionIndex];
        if (initialColor) filterImages(initialColor);
      }
    }

    // Delay to ensure Flickity has initialized
    setTimeout(initialFilter, 250);
  }

  // --- Bootstrap ---
  function init() {
    var containers = document.querySelectorAll(
      '[data-section-type="product-template"]'
    );
    containers.forEach(function (container) {
      initVariantImageFilter(container);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Handle Shopify theme editor section reload
  document.addEventListener('shopify:section:load', function (e) {
    var container = e.target.querySelector(
      '[data-section-type="product-template"]'
    );
    if (container) initVariantImageFilter(container);
  });
})();
